import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import api from '../config/api'

const BrandingContext = createContext(null)

const DEFAULT_BRANDING = {
  firmName: 'Client Portal',
  tagline: '',
  logo: null,
  primaryColor: '#2563eb',
  secondaryColor: '#8b5cf6',
  accentColor: '#10b981'
}

function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result
    ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
    : null
}

function darkenHex(hex, amount = 20) {
  const num = parseInt(hex.replace('#', ''), 16)
  const r = Math.max(0, (num >> 16) - amount)
  const g = Math.max(0, ((num >> 8) & 0x00ff) - amount)
  const b = Math.max(0, (num & 0x0000ff) - amount)
  return `#${(r << 16 | g << 8 | b).toString(16).padStart(6, '0')}`
}

function lightenHex(hex, amount = 30) {
  const num = parseInt(hex.replace('#', ''), 16)
  const r = Math.min(255, (num >> 16) + amount)
  const g = Math.min(255, ((num >> 8) & 0x00ff) + amount)
  const b = Math.min(255, (num & 0x0000ff) + amount)
  return `#${(r << 16 | g << 8 | b).toString(16).padStart(6, '0')}`
}

function applyBrandingCSS(branding) {
  const root = document.documentElement
  const primary = branding.primaryColor || DEFAULT_BRANDING.primaryColor

  root.style.setProperty('--primary', primary)
  root.style.setProperty('--primary-dark', darkenHex(primary))
  root.style.setProperty('--primary-light', lightenHex(primary))

  const rgb = hexToRgb(primary)
  if (rgb) {
    root.style.setProperty('--primary-rgb', rgb)
  }
}

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(DEFAULT_BRANDING)
  const [brandingLoaded, setBrandingLoaded] = useState(false)

  const fetchBranding = useCallback(async (tenantId) => {
    if (!tenantId) return

    try {
      const response = await api.get(`/branding/${tenantId}`)
      const data = response.data.data
      setBranding(data)
      applyBrandingCSS(data)
    } catch (err) {
      console.warn('Failed to fetch tenant branding:', err.message)
    } finally {
      setBrandingLoaded(true)
    }
  }, [])

  // On mount, try to get tenantId from saved client data
  useEffect(() => {
    const savedClient = localStorage.getItem('portal_client')
    if (savedClient) {
      try {
        const client = JSON.parse(savedClient)
        if (client.tenantId) {
          fetchBranding(client.tenantId)
          return
        }
      } catch { /* ignore */ }
    }
    setBrandingLoaded(true)
  }, [fetchBranding])

  const value = {
    branding,
    brandingLoaded,
    fetchBranding
  }

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  )
}

export function useBranding() {
  const context = useContext(BrandingContext)
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider')
  }
  return context
}

export default useBranding
